import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageTitle } from "@/contexts/page-title-context";
import { VStack } from "@/components/ui/v-stack";
import { TabContainer, TabList, Tab, TabPanel } from "@/components/ui/tab";
import { StatusTab } from "@/components/admin/system/status-tab";
import { JobsTab } from "@/components/admin/system/jobs-tab";
import { LogsTab } from "@/components/admin/system/logs-tab";

type TabKey = "status" | "jobs" | "logs";

const TAB_KEYS: TabKey[] = ["status", "jobs", "logs"];

function AdminSystemPageInner() {
  const search = useSearch({ strict: false }) as { tab?: TabKey };
  const navigate = useNavigate();
  const { t } = useTranslation("admin.system");
  const [activeTab, setActiveTab] = useState<TabKey>(search.tab ?? "status");

  const switchTab = (tab: TabKey) => {
    setActiveTab(tab);
    void navigate({ to: "/admin/system", search: { tab }, replace: true });
  };

  return (
    <VStack gap="sm" data-testid="admin-system-page">
      <PageTitle>{t("title")}</PageTitle>

      <TabContainer>
        <TabList>
          {TAB_KEYS.map((key) => (
            <Tab
              key={key}
              active={activeTab === key}
              onClick={() => switchTab(key)}
            >
              {t(`tabs.${key}`)}
            </Tab>
          ))}
        </TabList>
      </TabContainer>

      <TabPanel>
        {activeTab === "status" ? <StatusTab /> : null}
        {activeTab === "jobs" ? <JobsTab /> : null}
        {activeTab === "logs" ? <LogsTab /> : null}
      </TabPanel>
    </VStack>
  );
}

function AdminSystemPage() {
  return (
    <Suspense>
      <AdminSystemPageInner />
    </Suspense>
  );
}

export const Route = createFileRoute("/_app/admin/system")({
  component: AdminSystemPage,
});
